import { useState } from 'react';

import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { COLOR_TOKENS } from '@backrs_ui/common-ui/src/designSystem/colors/colors';
import { TYPOGRAPHY_VARIANTS } from '@backrs_ui/common-ui/src/designSystem/typography/typography';
import { formatCurrency } from '@backrs_ui/common-ui/src/utils/stringFormatters.util';

import {
  Box,
  ClickAwayListener,
  SxProps,
  Theme,
  Typography,
  Tooltip as MUITooltip,
} from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import { Line } from 'react-chartjs-2';

import { ROILineChartHeader } from './ROILineChartHeader';
import { AppIcon } from '../..';

const ROI_LINE_CHART_STRINGS = appStrings.modules.charts.returnOnInvestment;

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  annotationPlugin,
);

type ROILineChartProps = Readonly<{
  width?: number;
  height?: number;
  averageData: number[];
  actualData: number[];
  title: string;
  total_roi: number;
  average_roi: number;
}>;

export const ROILineChart = ({
  averageData,
  actualData,
  title,
  total_roi,
  average_roi,
}: ROILineChartProps) => {
  const [isLegendToolTipOpen, setIsLegendToolTipOpen] = useState(false);

  const years = actualData.map((_, index) => index);

  const data = {
    labels: years,
    datasets: [
      {
        label: ROI_LINE_CHART_STRINGS.average,
        data: averageData,
        borderColor: COLOR_TOKENS['Brand/400'],
        borderDash: [5, 5],
        tension: 0.3,
        pointRadius: 0,
        pointHoverRadius: 0,
        hoverBorderWidth: 0,
      },
      {
        label: title,
        data: actualData,
        borderColor: COLOR_TOKENS['Brand/500'],
        tension: 0.3,
        pointRadius: 0,
        pointHoverRadius: 0,
        hoverBorderWidth: 0,
      },
    ],
  };

  const lowestTick = Math.min(...actualData);
  const highestTick = Math.max(...actualData);

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        title: {
          display: true,
          text: ROI_LINE_CHART_STRINGS.yAxisLabel,
          color: COLOR_TOKENS['Colors/Text/text-tertiary'],
          font: {
            size: 12,
            family: 'Sarabun, sans-serif',
          },
        },
        grid: {
          tickColor: 'transparent',
          display: true,
          color: (context) => {
            const value = context.tick.value;

            if (value === 0) {
              return COLOR_TOKENS['Colors/Foreground/fg-tertiary'];
            }

            return COLOR_TOKENS['Colors/Border/border-tertiary'];
          },
        },
        border: {
          display: false,
        },
        min: Math.min(lowestTick - 50000, -250000),
        max: Math.max(highestTick + 50000, 1000000),
        afterBuildTicks: (axis) => {
          // we will always have at least -250 -> 1m, but we will increase/decrease in 250k increments to the highest/lowest tick
          const minTick = Math.min(Math.floor(lowestTick / 250000) * 250000, -250000);
          const maxTick = Math.max(Math.ceil(highestTick / 250000) * 250000, 1000000);

          const tickCount = (maxTick - minTick) / 250000 + 1;

          axis.ticks = Array.from({ length: tickCount }, (_, i) => ({
            value: minTick + i * 250000,
          }));
        },
        ticks: {
          callback: (value) => {
            const valueAsNumber = Number(value);

            // format the tick like $250k, $500k, $750k, $1m, $1.25m, $1.5m, $1.75m, $2m
            if (valueAsNumber % 250000 === 0) {
              const absValue = Math.abs(valueAsNumber);
              const suffix = absValue >= 1000000 ? 'M' : 'K';
              const formattedValue = absValue >= 1000000 ? absValue / 1000000 : absValue / 1000;

              return `${valueAsNumber < 0 ? '-' : ''}$${formattedValue}${suffix} `;
            }

            return '';
          },
          font: {
            size: 12,
            weight: 'lighter',
            family: 'Sarabun, sans-serif',
          },
        },
      },
      x: {
        title: {
          display: true,
          text: ROI_LINE_CHART_STRINGS.xAxisLabel,
          color: COLOR_TOKENS['Colors/Text/text-tertiary'],
          padding: {
            top: 16,
            bottom: 0,
          },
          font: {
            size: 12,
            family: 'Sarabun, sans-serif',
          },
        },
        grid: {
          display: false,
        },
        border: {
          display: false,
        },
        ticks: {
          align: 'end',
          font: {
            size: 12,
            weight: 'lighter',
            family: 'Sarabun, sans-serif',
          },
          // we only want to show every other tick, but not 0
          callback: (_, index) => {
            if (index >= 2 && index % 2 === 0) {
              return index;
            }

            return '';
          },
        },
      },
    },
    interaction: {
      intersect: false,
      mode: 'index',
    },
    plugins: {
      tooltip: {
        enabled: true,
        displayColors: false,
        padding: {
          top: 8,
          bottom: 8,
          left: 12,
          right: 12,
        },
        caretSize: 0,
        yAlign: 'bottom',
        callbacks: {
          title: function (context) {
            return `${ROI_LINE_CHART_STRINGS.xAxisLabel} ${context[0].label}`;
          },
          label: function (context) {
            const label = context.dataset.label || '';
            const value = context.parsed.y;

            // Format the value as currency
            return `${label}: ${formatCurrency(value)}`;
          },
        },
      },
      legend: {
        labels: {
          generateLabels: () => [],
        },
      },
      title: {
        display: false,
      },
    },
    hover: {
      mode: 'index',
      intersect: false,
    },
  };

  type LegendItemProps = Readonly<{
    title: string;
    color: string;
    isDashed: boolean;
    tooltip?: string;
  }>;

  const LegendItem = ({ title, color, isDashed, tooltip }: LegendItemProps) => {
    return (
      <Box sx={legendItemSx}>
        <Box sx={legendItemColorSx(color)} />
        {isDashed && <Box sx={legendItemColorSx(color)} />}
        <Typography
          variant="text-sm-regular"
          color={({ backrs }) => backrs.colors['Colors/Text/text-tertiary']}
        >
          {title}
        </Typography>
        {tooltip && (
          <ClickAwayListener onClickAway={() => setIsLegendToolTipOpen(false)}>
            <MUITooltip
              open={isLegendToolTipOpen}
              onClose={() => setIsLegendToolTipOpen(false)}
              placement="top"
              arrow
              disableFocusListener
              disableHoverListener
              disableTouchListener
              title={ROI_LINE_CHART_STRINGS.tooltip}
              sx={tooltipSx}
            >
              <Box sx={tooltipIconSx} onClick={() => setIsLegendToolTipOpen((prev) => !prev)}>
                <AppIcon
                  iconName="InfoCircle"
                  diameter="16px"
                  color="Colors/Foreground/fg-quinary"
                />
              </Box>
            </MUITooltip>
          </ClickAwayListener>
        )}
      </Box>
    );
  };

  return (
    <Box sx={containerSx}>
      <ROILineChartHeader total_roi={total_roi} average_roi={average_roi} />

      <Box sx={legendSx}>
        <LegendItem title={title} color={COLOR_TOKENS['Brand/500']} isDashed={false} />
        <LegendItem
          title={ROI_LINE_CHART_STRINGS.rankedAverageLegend}
          color={COLOR_TOKENS['Brand/400']}
          isDashed={true}
          tooltip={ROI_LINE_CHART_STRINGS.rankedAverageTooltip}
        />
      </Box>

      <Box sx={chartContainerSx}>
        <Line data={data} options={options} />
      </Box>
    </Box>
  );
};

const chartContainerSx: SxProps<Theme> = {
  width: '100%',
  height: '100%',
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  '@media (max-width: 768px)': {
    width: '100%',
  },
  '@media (max-width: 400px)': {
    width: '100%',
  },
  height: { xs: 'auto', lg: '405px' },
  backgroundColor: backrs.colors['Colors/Base/White'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-primary']}`,
  boxShadow: backrs.shadows['shadow-xs'],
  borderRadius: backrs.radius['radius-xl'],
  padding: {
    xs: backrs.spacing['spacing-xl'],
    sm: backrs.spacing['spacing-2xl'],
  },
});

const legendSx: SxProps<Theme> = ({ backrs }) => ({
  alignSelf: { xs: 'flex-end', sm: 'flex-end' },
  display: 'flex',
  alignItems: { xs: 'flex-end', sm: 'center' },
  flexDirection: { xs: 'column', sm: 'row' },

  gap: backrs.spacing['spacing-lg'],
  fontSize: TYPOGRAPHY_VARIANTS['text-sm-regular'].fontSize,
  mb: { xs: backrs.spacing['spacing-2xl'], sm: backrs.spacing['spacing-none'] },
});

const legendItemSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
});

const legendItemColorSx =
  (color: string): SxProps<Theme> =>
  ({ backrs }) => ({
    width: '6px',
    height: '4px',
    borderRadius: backrs.radius['radius-full'],
    backgroundColor: color,
    alignSelf: 'center',
    justifySelf: 'center',
    marginTop: '2px',
  });

const tooltipIconSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  marginLeft: backrs.spacing['spacing-xxs'],
});

const tooltipSx: SxProps<Theme> = ({ backrs }) => ({
  marginLeft: backrs.spacing['spacing-xs'],
});
